import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  motion,
  AnimatePresence,
} from 'framer-motion';
import {
  CheckCircleIcon,
  ArrowRightIcon,
  ChevronLeft,
  ChevronRight,
  HistoryIcon,
  XIcon,
  ArrowLeftIcon,
  CrownIcon,
  TrophyIcon,
  MoonIcon,
  SunIcon,
  AlertCircleIcon,
} from 'lucide-react';

import Button from '../components/Button';
import GlassPanel from '../components/GlassPanel';
import { Category } from '../types/category';
import { categoryApi } from '../services/categoryApi';
import { sessionApi } from '../services/sessionApi';
import { Session, SessionStatus, SessionType } from '../types/session';
import { questionApi } from '../services/questionApi';
import GameLoadingAnimation from './GameLoadingAnimation';
import ParticleBackground from '../components/ParticleBackground';
import { useTheme, themeStyles } from '../context/ThemeContext';
import { userApi } from '../services/userApi';

interface Team {
  name: string;
}

const TeamSetup: React.FC = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const currentTheme = themeStyles[theme];
  const isAuthenticated = !!localStorage.getItem('token');

  // -------------------- STATES --------------------
  const [sessions, setSessions] = useState<Session[]>([]);
  const [isSessionSidebarOpen, setIsSessionSidebarOpen] = useState(false);

  const [team1, setTeam1] = useState<Team>({ name: '' });
  const [team2, setTeam2] = useState<Team>({ name: '' });

  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [sessionType, setSessionType] = useState<SessionType>(SessionType.PAYED);
  const [freeGameRemaining, setFreeGameRemaining] = useState<number>(0);
  const [gameRemaining, setGameRemaining] = useState<number>(0);

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [operationResult, setOperationResult] =
    useState<{ status: number; message: string } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [gameLoading, setGameLoading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Constants
  const itemsPerPage = 15; // categories per page
  const selectedCategoriesCount = selectedCategories.length;
  const isMaxCategoriesSelected = selectedCategoriesCount === 6;

  // -------------------- LOGIC: FETCH & SIDEBAR --------------------
  // Check user game credits
  useEffect(() => {
    const checkGameCredits = async () => {
      if (isAuthenticated) {
        try {
          const freeGames = await userApi.getFreeGames();
          const paidGames = await userApi.getRemainingGames();
          
          setFreeGameRemaining(freeGames);
          setGameRemaining(paidGames);
          
          // Set initial session type based on available credits
          if (freeGames > 0) {
            setSessionType(SessionType.FREE);
          } else if (paidGames > 0) {
            setSessionType(SessionType.PAYED);
          }
        } catch (error) {
          console.error('Error fetching game credits:', error);
        }
      }
    };
    
    checkGameCredits();
  }, [isAuthenticated]);

  // Session logic
  const fetchSessions = useCallback(async () => {
    try {
      const response = await sessionApi.getSessionsByUserId();
      // Sessions are already sorted by updatedAt on the backend (newest first)
      
      // Enhance sorting on the frontend using local session access history
      const sortedSessions = [...response];
      
      // Get the most recently accessed sessions from localStorage
      const recentlyAccessedSessions = JSON.parse(localStorage.getItem('recentlyAccessedSessions') || '[]');
      
      // Sort sessions with recently accessed ones first, then by backend order
      sortedSessions.sort((a, b) => {
        const aIndex = recentlyAccessedSessions.indexOf(a.sessionId);
        const bIndex = recentlyAccessedSessions.indexOf(b.sessionId);
        
        // If both sessions are in the recent list, sort by their position in the list
        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex;
        }
        // If only one session is in the recent list, it comes first
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        
        // Otherwise keep the original order (which is already sorted by updatedAt on the backend)
        return 0;
      });
      
      setSessions(sortedSessions);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  }, []);

  const toggleSessionSidebar = useCallback(() => {
    setIsSessionSidebarOpen((prev) => !prev);
  }, []);

  const handleSessionClick = useCallback(
    (session: Session) => {
      if (session.sessionStatus === SessionStatus.IN_PROGRESS) {
        // Track this session as recently accessed
        const recentlyAccessedSessions = JSON.parse(localStorage.getItem('recentlyAccessedSessions') || '[]');
        
        // Remove the session ID if it already exists in the list
        const filteredSessions = recentlyAccessedSessions.filter(
          (sessionId: string) => sessionId !== session.sessionId
        );
        
        // Add this session ID to the beginning of the list (most recent)
        const updatedSessions = [session.sessionId, ...filteredSessions];
        
        // Keep only the most recent 10 sessions
        const trimmedSessions = updatedSessions.slice(0, 10);
        
        // Save back to localStorage
        localStorage.setItem('recentlyAccessedSessions', JSON.stringify(trimmedSessions));
        
        // Navigate to the session
        navigate(`/game/${session.sessionId}`);
      }
    },
    [navigate]
  );

  // Fetch sessions if sidebar is opened
  useEffect(() => {
    if (isSessionSidebarOpen) {
      fetchSessions();
    }
  }, [isSessionSidebarOpen, fetchSessions]);

  // Category logic
  const fetchCategories = useCallback(
    async (page: number) => {
      const abortController = new AbortController();
      try {
        setIsLoading(true);
        const response = await categoryApi.getAllCategoriesForUser(
          page,
          itemsPerPage,
          sessionType === SessionType.FREE ? 'FREE' : 'PAYED'
        );
        if (response) {
          setCategories(response.content);
          setTotalPages(response.totalPages);
        }
      } catch (error: any) {
        if (!abortController.signal.aborted) {
          setOperationResult({
            status: error.response?.status || 500,
            message:
              error.response?.data?.message ||
              error.message ||
              'Failed to fetch categories',
          });
        }
      } finally {
        setIsLoading(false);
      }
      return () => abortController.abort();
    },
    [itemsPerPage, sessionType]
  );

  // Only fetch categories when sessionType changes or page changes
  // This will prevent duplicate calls
  useEffect(() => {
    // Skip initial render when sessionType is not yet set based on credits
    if (isAuthenticated && freeGameRemaining === 0 && gameRemaining === 0) {
      return;
    }
    fetchCategories(currentPage);
  }, [currentPage, fetchCategories, sessionType, isAuthenticated, freeGameRemaining, gameRemaining]);

  // -------------------- CATEGORY SELECTION --------------------
  const toggleCategory = useCallback(
    (id: string) => {
      setSelectedCategories((prev) => {
        if (prev.includes(id)) {
          // remove
          return prev.filter((catId) => catId !== id);
        }
        // add if not authenticated or there's usage left and total < 6
        const cat = categories.find((c) => c.categoryId === id);
        if ((!isAuthenticated || (cat && cat.numberOfGamesLeft > 0)) && prev.length < 6) {
          return [...prev, id];
        }
        return prev;
      });
      // Clear validation error when categories change
      setValidationError(null);
    },
    [categories, isAuthenticated]
  );

  // -------------------- CREATE SESSION --------------------
  // Define createSession first so it can be used in validateAndCreateSession
  const createSession = useCallback(async () => {
    const newSession: Session = {
      sessionId: '',
      userId: '',
      team1name: team1.name,
      team2name: team2.name,
      team1score: 0,
      team2score: 0,
      gameCategories: selectedCategories,
      sessionStatus: SessionStatus.IN_PROGRESS,
      powerups: { team1: 0, team2: 0 },
      categoryQuestionsMap: new Map(),
      team1Powerups: [],
      team2Powerups: [],
      sessionType: sessionType,
    };

    try {
      setGameLoading(true);
      const response = await sessionApi.createSession(newSession);
      if (response.sessionId) {
        navigate(`/game/${response.sessionId}`);
      }
    } catch (error) {
      console.error('Session creation failed', error);
    } finally {
      setGameLoading(false);
    }
  }, [team1, team2, selectedCategories, navigate, sessionType]);
  
  const validateAndCreateSession = useCallback(() => {
    // Check if teams are complete
    if (!team1.name || !team2.name) {
      setValidationError('يرجى إدخال أسماء الفريقين');
      return;
    }

    // Check if 6 categories are selected
    if (selectedCategoriesCount !== 6) {
      setValidationError('يجب عليك اختيار 6 فئات للعب');
      return;
    }

    // Check if user is authenticated, redirect to register if not
    if (!isAuthenticated) {
      navigate('/register');
      return;
    }

    // Check if user has enough credits for the selected session type
    if (sessionType === SessionType.FREE && freeGameRemaining <= 0) {
      setValidationError('ليس لديك جلسات مجانية كافية');
      return;
    }

    if (sessionType === SessionType.PAYED && gameRemaining <= 0) {
      setValidationError('ليس لديك جلسات مدفوعة كافية');
      return;
    }

    // Clear previous validation errors
    setValidationError(null);
    
    // Create session
    createSession();
  }, [team1.name, team2.name, selectedCategoriesCount, isAuthenticated, createSession, navigate, sessionType, freeGameRemaining, gameRemaining]);

  // -------------------- UI MEMOS --------------------
  // Pagination
  const PaginationControls = useMemo(() => {
    if (totalPages <= 1) return null;
    return (
      <div className="flex justify-center items-center gap-8 mt-10 mb-8">
        <Button
          variant="secondary"
          onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
          disabled={currentPage === 0}
          icon={<ChevronLeft className="w-6 h-6" />}
          className="px-6 py-3 text-xl"
        >
          السابق
        </Button>
        <span className={`text-xl sm:text-2xl ${currentTheme.text}`}>
          صفحة {currentPage + 1} من {totalPages}
        </span>
        <Button
          variant="secondary"
          onClick={() =>
            setCurrentPage((p) => Math.min(totalPages - 1, p + 1))
          }
          disabled={currentPage === totalPages - 1}
          icon={<ChevronRight className="w-6 h-6" />}
          className="px-6 py-3 text-xl"
        >
          التالي
        </Button>
      </div>
    );
  }, [currentPage, totalPages, currentTheme]);

  // -------------------- RENDER --------------------
  return (
    <div className={`min-h-screen w-full overflow-hidden bg-gradient-to-br ${currentTheme.background} relative`}>
      {/* Background elements */}
      <ParticleBackground />

      {/* Main content container */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <header className="p-4 sm:p-6">
          <div className="w-full flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex gap-2 w-full sm:w-auto justify-start">
              <Button
                variant="secondary"
                onClick={() => navigate('/')}
                icon={<ArrowLeftIcon className="w-4 h-4" />}
                className={`${theme === 'dark' ? 'bg-white/10 hover:bg-white/20 text-white border-none' : ''}`}
              >
                العودة إلى الصفحة الرئيسية
              </Button>
              
              <Button
                variant="secondary"
                onClick={toggleTheme}
                icon={theme === 'dark' ? <SunIcon className="w-4 h-4" /> : <MoonIcon className="w-4 h-4" />}
                className={`${theme === 'dark' ? 'bg-white/10 hover:bg-white/20 text-white border-none' : ''}`}
              >
                {theme === 'dark' ? 'الوضع الفاتح' : 'الوضع الداكن'}
              </Button>
            </div>
            
            <div className="w-full sm:w-auto flex justify-end">
              {isAuthenticated && (
                <Button
                  variant="secondary"
                  onClick={toggleSessionSidebar}
                  icon={<HistoryIcon className="w-4 h-4" />}
                  className={`${theme === 'dark' ? 'bg-white/10 hover:bg-white/20 text-white border-none' : ''}`}
                >
                  عرض الجلسات السابقة
                </Button>
              )}
            </div>
          </div>
        </header>

        {/* Main content */}
        <main className="flex-1 p-4 sm:p-6">
          <div className="w-full h-full max-w-[1400px] mx-auto">
            {/* Title and progress */}
            <div className="text-center mb-8 sm:mb-10">
              <h1 className={`text-4xl sm:text-6xl font-bold ${currentTheme.text} mb-6`}>اختر فئات اللعبة</h1>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
                <div className={`${theme === 'dark' ? 'bg-white/10' : 'bg-gray-200/60'} rounded-full px-8 py-3`}>
                  <span className={`font-medium text-xl sm:text-2xl ${currentTheme.text}`}>
                    {selectedCategoriesCount} / 6 فئات مختارة
                  </span>
                </div>
                {isAuthenticated && (
                  <div className={`${theme === 'dark' ? 'bg-white/10' : 'bg-gray-200/60'} rounded-full px-6 py-3 flex items-center gap-4 flex-wrap justify-center`}>
                    <span className={`font-medium text-xl ${currentTheme.text}`}>
                      نوع الجلسة:
                    </span>
                    <div className="flex gap-3 items-center">
                      <Button
                        variant={sessionType === SessionType.FREE ? "primary" : "secondary"}
                        size="small"
                        onClick={() => {
                          if (sessionType !== SessionType.FREE) {
                            setSessionType(SessionType.FREE);
                            setSelectedCategories([]);
                            setCurrentPage(0);
                          }
                        }}
                        disabled={freeGameRemaining <= 0}
                        className={`px-4 py-2 text-lg ${freeGameRemaining <= 0 ? 'opacity-50 cursor-not-allowed' : ''} 
                          ${sessionType === SessionType.FREE ? 'ring-2 ring-offset-2 ring-offset-slate-50 dark:ring-offset-slate-900 ring-blue-500 font-bold transform scale-105' : ''}`}
                      >
                        {sessionType === SessionType.FREE && <CheckCircleIcon className="w-4 h-4 inline mr-1" />}
                        مجانية ({freeGameRemaining})
                      </Button>
                      <Button
                        variant={sessionType === SessionType.PAYED ? "primary" : "secondary"}
                        size="small"
                        onClick={() => {
                          if (sessionType !== SessionType.PAYED) {
                            setSessionType(SessionType.PAYED);
                            setSelectedCategories([]);
                            setCurrentPage(0);
                          }
                        }}
                        disabled={gameRemaining <= 0}
                        className={`px-4 py-2 text-lg ${gameRemaining <= 0 ? 'opacity-50 cursor-not-allowed' : ''} 
                          ${sessionType === SessionType.PAYED ? 'ring-2 ring-offset-2 ring-offset-slate-50 dark:ring-offset-slate-900 ring-blue-500 font-bold transform scale-105' : ''}`}
                      >
                        {sessionType === SessionType.PAYED && <CheckCircleIcon className="w-4 h-4 inline mr-1" />}
                        مدفوعة ({gameRemaining})
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Categories grid */}
            {isLoading ? (
              <div className="flex justify-center p-6">
                <div className={`animate-spin rounded-full h-16 w-16 border-b-2 ${theme === 'dark' ? 'border-white' : 'border-gray-700'}`} />
              </div>
            ) : (
              <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-8 px-4 sm:px-8">
                {categories.map((category, index) => {
                  const isSelected = selectedCategories.includes(category.categoryId || '');
                  const isDisabled = isAuthenticated && category.numberOfGamesLeft === 0;
                  const isDimmed = isMaxCategoriesSelected && !isSelected;

                  return (
                    <div
                      key={category.categoryId}
                      className={`cursor-pointer transition-transform duration-200 ${!isDisabled && !isDimmed ? 'hover:scale-105' : ''}`}
                      onClick={() => !isDimmed && toggleCategory(category.categoryId || '')}
                    >
                      <GlassPanel
                        className={`relative overflow-hidden transition-all duration-300 p-0
                          ${isSelected ? `ring-4 ring-[#4B7BFF] ${theme === 'dark' ? 'ring-offset-[#16213e]' : 'ring-offset-[#F7F9FB]'} ring-offset-4` : ''}
                          ${isDisabled || isDimmed ? 'opacity-50 pointer-events-none' : 'hover:shadow-lg'}
                          ${theme === 'dark' ? 'bg-white/10' : 'bg-white/70'} backdrop-blur-sm
                          flex flex-col
                        `}
                      >
                        {isAuthenticated && (
                          <div className="absolute top-3 left-3 z-10 bg-[#4B7BFF] text-white text-lg px-3 py-1.5 rounded-full">
                            {category.numberOfGamesLeft} لعبة
                          </div>
                        )}

                        {isSelected && (
                          <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                            <div className={`${theme === 'dark' ? 'bg-white/20' : 'bg-blue-500/20'} backdrop-blur-[2px] absolute inset-0`}></div>
                            <div className={`rounded-full ${theme === 'dark' ? 'bg-white/90' : 'bg-blue-600'} p-3.5 shadow-lg transform`}>
                              <CheckCircleIcon className="text-white" size={38} strokeWidth={3} />
                            </div>
                          </div>
                        )}

                        {/* Image takes up the entire top section - 20% larger */}
                        <div className="w-full h-40 overflow-hidden">
                          <img
                            src={category.thumbnailUrl || ''}
                            alt={category.categoryName || ''}
                            className="w-full h-full object-cover"
                          />
                        </div>

                        {/* Title underneath the image */}
                        <div className="p-4">
                          <h3 className={`text-center font-bold text-lg sm:text-xl ${currentTheme.text}`}>
                            {category.categoryName}
                          </h3>
                        </div>
                      </GlassPanel>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {PaginationControls}

            {/* Teams section */}
            <div className="mt-12 sm:mt-16 grid grid-cols-1 md:grid-cols-2 gap-8 px-4 sm:px-8">
              {[team1, team2].map((team, index) => (
                <div key={index}>
                  <GlassPanel className={`p-6 sm:p-8 h-full ${theme === 'dark' ? 'bg-white/10' : 'bg-white/70'} backdrop-blur-sm`}>
                    <div className="flex items-center gap-4 mb-6 sm:mb-8">
                      {index === 0 ? (
                        <CrownIcon className="w-8 h-8 sm:w-10 sm:h-10 text-yellow-400" />
                      ) : (
                        <TrophyIcon className="w-8 h-8 sm:w-10 sm:h-10 text-yellow-400" />
                      )}
                      <h3 className={`text-2xl sm:text-3xl font-bold ${currentTheme.text}`}>
                        {index === 0 ? 'الفريق الأول' : 'الفريق الثاني'}
                      </h3>
                    </div>
                    <div className="mb-6 sm:mb-8">
                      <label className={`block ${theme === 'dark' ? 'text-white/80' : 'text-gray-600'} mb-3 text-xl`}>
                        اسم الفريق
                      </label>
                      <input
                        type="text"
                        value={team.name}
                        onChange={(e) => {
                          if (index === 0) setTeam1({ name: e.target.value });
                          else setTeam2({ name: e.target.value });
                          setValidationError(null);
                        }}
                        className={`w-full ${theme === 'dark' ? 'bg-white/20 border-white/30 text-white placeholder-white/50' : 'bg-white/80 border-gray-300 text-gray-800 placeholder-gray-400'} 
                                   border rounded-lg px-5 py-4 focus:outline-none focus:ring-2 focus:ring-[#4B7BFF] text-xl`}
                        placeholder="أدخل اسم الفريق"
                        maxLength={20}
                      />
                    </div>
                  </GlassPanel>
                </div>
              ))}
            </div>

            {/* Validation error message */}
            {validationError && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`mt-8 p-4 rounded-lg max-w-md mx-auto text-center bg-red-500/20 ${theme === 'dark' ? 'text-red-300' : 'text-red-700'}`}
              >
                <div className="flex items-center justify-center gap-2">
                  <AlertCircleIcon className="w-5 h-5" />
                  <p className="font-medium">{validationError}</p>
                </div>
              </motion.div>
            )}

            {/* Start game button */}
            <div className="mt-12 sm:mt-16 flex justify-center">
              <Button
                variant="gradient"
                icon={<ArrowRightIcon className="w-6 h-6" />}
                onClick={validateAndCreateSession}
                className="px-8 sm:px-10 py-4 sm:py-5 text-xl sm:text-2xl"
              >
                {isAuthenticated ? 'ابدأ اللعبة' : 'تسجيل الدخول للعب'}
              </Button>
            </div>
          </div>
        </main>
      </div>

      {/* Session Sidebar */}
      <AnimatePresence>
        {isSessionSidebarOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className={`fixed right-0 top-0 h-full w-80 ${theme === 'dark' ? 'bg-[#1a1a2e]/95 border-white/10' : 'bg-white/95 border-gray-300'} backdrop-blur-xl z-50 p-4 border-l shadow-xl overflow-y-auto`}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className={`text-xl font-bold ${currentTheme.text}`}>
                الجلسات السابقة
              </h2>
              <Button
                variant="gradient"
                size="small"
                onClick={toggleSessionSidebar}
                icon={<XIcon className="w-4 h-4 text-white" />}
                className={theme === 'dark' ? 'bg-white/10 hover:bg-white/20' : ''}
              >
                {''}
              </Button>
            </div>

            <div className="mb-4">
              <Button
                variant="gradient"
                size="small"
                onClick={async () => {
                  try {
                    await questionApi.clearQuestionRecord();
                    fetchCategories(currentPage);
                    setOperationResult({
                      status: 200,
                      message: 'تم مسح سجل الأسئلة السابقة بنجاح',
                    });
                  } catch (error: any) {
                    setOperationResult({
                      status: error.response?.status || 500,
                      message:
                        error.response?.data?.message ||
                        error.message ||
                        'Failed to clear question record',
                    });
                  }
                }}
                className={`w-full ${theme === 'dark' ? 'bg-white/10 hover:bg-white/20' : ''} text-lg px-5 py-2.5`}
              >
                مسح سجل الأسئلة
              </Button>
            </div>

            <div className="space-y-4">
              {sessions.map((session) => (
                <div
                  key={session.sessionId}
                  onClick={() => handleSessionClick(session)}
                  className="cursor-pointer"
                >
                  <GlassPanel
                    className={`p-4 transition-opacity ${theme === 'dark' ? 'bg-white/10' : 'bg-white/80'} backdrop-blur-sm
                      ${session.sessionStatus !== 'IN_PROGRESS'
                        ? 'opacity-60'
                        : 'hover:opacity-90'
                      }`}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className={`font-medium ${currentTheme.text}`}>
                          {session.team1name} vs {session.team2name}
                        </p>
                        <p className={`text-sm ${theme === 'dark' ? 'text-white/60' : 'text-gray-500'}`}>
                          {session.sessionStatus === 'IN_PROGRESS'
                            ? 'جارية'
                            : 'مكتملة'}
                        </p>
                      </div>
                    </div>
                  </GlassPanel>
                </div>
              ))}
              {sessions.length === 0 && (
                <p className={`text-center ${theme === 'dark' ? 'text-white/60' : 'text-gray-600'}`}>
                  لا توجد جلسات سابقة
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Game loading overlay */}
      {gameLoading && (
        <div className={`fixed inset-0 ${theme === 'dark' ? 'bg-[#1a1a2e]/95' : 'bg-white/80'} backdrop-blur-sm z-50 flex justify-center items-center`}>
          <GameLoadingAnimation />
        </div>
      )}

      {/* Operation Result */}
      {operationResult && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`fixed top-4 left-1/2 transform -translate-x-1/2 p-4 rounded-lg mb-4 max-w-md mx-auto text-center z-50 ${
            operationResult.status === 200
              ? 'bg-green-500/20 text-green-700'
              : 'bg-red-500/20 text-red-700'
          }`}
        >
          <p className="font-medium">{operationResult.message}</p>
        </motion.div>
      )}
    </div>
  );
};

export default React.memo(TeamSetup);
